import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, Plus, Trash2, Download, Search, Send, X } from "lucide-react";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { ResultBlock } from "@/components/GuestImportDialog";

const TEMPLATE = "name,email,phone,note\nJane Doe,jane@example.com,+1 555 0100,VIP\n";

export default function Contacts() {
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("");
    const [showAdd, setShowAdd] = useState(false);
    const [showInvite, setShowInvite] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const fileRef = useRef();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/contacts");
            setContacts(data);
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const filtered = contacts.filter(c => {
        const q = filter.trim().toLowerCase();
        if (!q) return true;
        return c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q) || c.phone.toLowerCase().includes(q);
    });

    const remove = async (id) => {
        try {
            await api.delete(`/contacts/${id}`);
            setContacts(cs => cs.filter(c => c.id !== id));
            toast.success("Contact removed");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to remove");
        }
    };

    const downloadTemplate = () => {
        const blob = new Blob([TEMPLATE], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "contacts-template.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    const upload = async (file) => {
        if (!file) return;
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/contacts/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setImportResult(data);
            await load();
            toast.success(`${data.added} added${data.skipped ? ` · ${data.skipped} skipped` : ""}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Import failed");
        }
    };

    return (
        <div className="min-h-screen flex flex-col">
            <Header />
            <main className="flex-1 max-w-[1100px] w-full mx-auto px-6 lg:px-10 py-8 lg:py-12">
                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-8">
                    <div>
                        <p className="text-[10px] tracking-[0.2em] uppercase text-[#888885] mb-2">Master list</p>
                        <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-[#111110]" data-testid="contacts-title">Contacts</h1>
                        <p className="text-[#666664] text-sm mt-2">External guests you can invite to any of your activities in one click.</p>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden"
                            onChange={(e) => upload(e.target.files?.[0])} data-testid="contacts-import-input" />
                        <Button variant="outline" onClick={downloadTemplate}
                            className="rounded-none border-[#E5E5E3] h-10 text-[#111110] hover:bg-[#F0F0EE] text-xs tracking-[0.15em] uppercase"
                            data-testid="contacts-template-btn">
                            <Download className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} /> Template
                        </Button>
                        <Button variant="outline" onClick={() => fileRef.current?.click()}
                            className="rounded-none border-[#E5E5E3] h-10 text-[#111110] hover:bg-[#F0F0EE] text-xs tracking-[0.15em] uppercase"
                            data-testid="contacts-import-btn">
                            <Upload className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} /> Import CSV
                        </Button>
                        <Button onClick={() => setShowAdd(true)}
                            className="rounded-none h-10 bg-[#111110] hover:bg-[#333333] text-white px-5 font-medium"
                            data-testid="add-contact-btn">
                            <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} /> New contact
                        </Button>
                    </div>
                </div>

                {importResult && (
                    <div className="mb-8 border border-[#E5E5E3] bg-white p-6">
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-[10px] tracking-[0.2em] uppercase text-[#888885]">Last import</p>
                            <button onClick={() => setImportResult(null)} className="text-[#888885] hover:text-[#111110]" data-testid="dismiss-import-result">
                                <X className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                        </div>
                        <ResultBlock result={importResult} testId="contacts-import-result" labels={{ added: "Added", skipped: "Skipped", total_rows: "Rows" }} />
                    </div>
                )}

                <div className="border border-[#E5E5E3] bg-white">
                    <div className="border-b border-[#E5E5E3] p-4 flex items-center gap-3">
                        <Search className="w-4 h-4 text-[#666664]" strokeWidth={1.5} />
                        <input
                            value={filter}
                            onChange={e => setFilter(e.target.value)}
                            placeholder="Search by name, email or phone"
                            data-testid="contacts-search"
                            className="flex-1 bg-transparent border-none outline-none text-sm text-[#111110]"
                        />
                        <span className="text-xs text-[#888885] tabular-nums" data-testid="contacts-count">{filtered.length} / {contacts.length}</span>
                    </div>
                    {loading ? (
                        <div className="p-12 text-center text-[#888885] text-sm tracking-[0.15em] uppercase">Loading…</div>
                    ) : filtered.length === 0 ? (
                        <div className="p-12 text-center">
                            <p className="text-[10px] tracking-[0.2em] uppercase text-[#888885] mb-2">Empty</p>
                            <p className="font-heading text-xl font-light text-[#111110]">{contacts.length === 0 ? "Your contact list is empty." : "No matches."}</p>
                            {contacts.length === 0 && <p className="text-sm text-[#666664] mt-2">Import a CSV or add contacts one-by-one to get started.</p>}
                        </div>
                    ) : (
                        <ul className="divide-y divide-[#E5E5E3]" data-testid="contacts-list">
                            {filtered.map(c => (
                                <li key={c.id} className="p-5 flex items-center gap-4" data-testid={`contact-${c.id}`}>
                                    <div className="w-10 h-10 border border-[#E5E5E3] bg-[#FAFAF9] inline-flex items-center justify-center font-heading text-sm text-[#111110]">
                                        {c.name.split(" ").map(s => s[0]).slice(0,2).join("").toUpperCase()}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-[#111110] truncate">{c.name}</p>
                                        <p className="text-xs text-[#666664] truncate">{c.email} · {c.phone}{c.note ? ` · ${c.note}` : ""}</p>
                                    </div>
                                    <button onClick={() => remove(c.id)} data-testid={`delete-contact-${c.id}`}
                                        className="text-[#888885] hover:text-[#111110] p-2">
                                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </main>

            <AddContactDialog open={showAdd} onOpenChange={setShowAdd} onSaved={load} />
        </div>
    );
}

function AddContactDialog({ open, onOpenChange, onSaved }) {
    const [form, setForm] = useState({ name: "", email: "", phone: "", note: "" });
    const [busy, setBusy] = useState(false);

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) return toast.error("Name, email and phone required");
        setBusy(true);
        try {
            await api.post("/contacts", form);
            toast.success("Contact added");
            setForm({ name: "", email: "", phone: "", note: "" });
            onSaved?.();
            onOpenChange(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not add");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md rounded-none border-[#E5E5E3] p-0 shadow-none" data-testid="add-contact-dialog">
                <form onSubmit={submit}>
                    <div className="p-8">
                        <DialogHeader className="text-left space-y-1">
                            <p className="text-[10px] tracking-[0.2em] uppercase text-[#888885]">New contact</p>
                            <DialogTitle className="font-heading text-2xl font-light tracking-tight text-[#111110]">Add to master list</DialogTitle>
                            <DialogDescription className="text-sm text-[#666664]">External guests stay in your personal list — only you see them.</DialogDescription>
                        </DialogHeader>
                        <div className="mt-6 space-y-4">
                            {[["name","Name"],["email","Email"],["phone","Phone"],["note","Note (optional)"]].map(([k,l]) => (
                                <div key={k}>
                                    <Label className="text-[10px] tracking-[0.2em] uppercase text-[#888885] mb-2 block font-medium">{l}</Label>
                                    <Input value={form[k]} onChange={e => set(k, e.target.value)}
                                        type={k==="email"?"email":k==="phone"?"tel":"text"}
                                        data-testid={`add-contact-${k}`}
                                        className="rounded-none border-[#E5E5E3] h-11 focus-visible:ring-1 focus-visible:ring-[#111110]" />
                                </div>
                            ))}
                        </div>
                    </div>
                    <DialogFooter className="border-t border-[#E5E5E3] px-8 py-5 bg-[#F9F9F8] flex-row gap-2 justify-end">
                        <Button variant="outline" type="button" onClick={() => onOpenChange(false)}
                            className="rounded-none border-[#E5E5E3] h-10 text-[#111110] hover:bg-[#F0F0EE]"
                            data-testid="add-contact-cancel">Cancel</Button>
                        <Button type="submit" disabled={busy}
                            className="rounded-none h-10 bg-[#111110] hover:bg-[#333333] text-white px-6"
                            data-testid="add-contact-save">Save contact</Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

// Reusable contact picker dialog used to invite contacts to a specific activity
export function InviteContactsDialog({ open, onOpenChange, activityId, onInvited }) {
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState({});
    const [filter, setFilter] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        if (!open) return;
        setSelected({}); setFilter(""); setResult(null);
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get("/contacts");
                setContacts(data);
            } finally { setLoading(false); }
        })();
    }, [open]);

    const toggle = (id) => setSelected(s => ({ ...s, [id]: !s[id] }));
    const filtered = contacts.filter(c => !filter.trim() || (c.name + " " + c.email).toLowerCase().includes(filter.toLowerCase()));
    const selectedIds = Object.keys(selected).filter(k => selected[k]);

    const invite = async () => {
        if (!selectedIds.length) return toast.error("Pick at least one contact");
        setBusy(true);
        try {
            const { data } = await api.post(`/activities/${activityId}/invite-contacts`, { contact_ids: selectedIds });
            setResult(data);
            onInvited?.(data.activity);
            toast.success(`${data.added} invited${data.waitlisted ? ` · ${data.waitlisted} waitlisted` : ""}${data.skipped ? ` · ${data.skipped} skipped` : ""}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Invite failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg rounded-none border-[#E5E5E3] p-0 shadow-none max-h-[80vh] grid-rows-[1fr_auto] overflow-hidden" data-testid="invite-contacts-dialog">
                <div className="overflow-hidden flex flex-col min-h-0">
                    <div className="p-8 pb-4">
                        <DialogHeader className="text-left space-y-1">
                            <p className="text-[10px] tracking-[0.2em] uppercase text-[#888885]">Invite from master list</p>
                            <DialogTitle className="font-heading text-2xl font-light tracking-tight text-[#111110]">Pick contacts to RSVP</DialogTitle>
                            <DialogDescription className="text-sm text-[#666664]">They&apos;ll be added to this activity (capacity respected).</DialogDescription>
                        </DialogHeader>
                        <div className="mt-4 border border-[#E5E5E3] flex items-center gap-3 px-3">
                            <Search className="w-4 h-4 text-[#666664]" strokeWidth={1.5} />
                            <input value={filter} onChange={e => setFilter(e.target.value)}
                                placeholder="Filter…" data-testid="invite-filter"
                                className="flex-1 bg-transparent border-none outline-none text-sm py-2.5" />
                        </div>
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto px-8 pb-4">
                        {loading ? <div className="text-sm text-[#888885] text-center py-8">Loading…</div>
                            : filtered.length === 0 ? <div className="text-sm text-[#888885] text-center py-8">No contacts.</div>
                            : (
                                <ul className="divide-y divide-[#E5E5E3] border border-[#E5E5E3]">
                                    {filtered.map(c => (
                                        <li key={c.id} className="p-3 flex items-center gap-3 cursor-pointer hover:bg-[#FAFAF9]"
                                            onClick={() => toggle(c.id)} data-testid={`invite-row-${c.id}`}>
                                            <Checkbox checked={!!selected[c.id]} onCheckedChange={() => toggle(c.id)} className="rounded-none border-[#888885]" />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm text-[#111110] font-medium truncate">{c.name}</p>
                                                <p className="text-xs text-[#666664] truncate">{c.email} · {c.phone}</p>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        {result && (
                            <div className="mt-4">
                                <ResultBlock result={result} testId="invite-result" labels={{ added: "Invited", waitlisted: "Waitlisted", skipped: "Skipped" }} />
                            </div>
                        )}
                    </div>
                </div>
                <DialogFooter className="border-t border-[#E5E5E3] px-8 py-5 bg-[#F9F9F8] flex-row gap-2 justify-end">
                    <Button variant="outline" onClick={() => onOpenChange(false)}
                        className="rounded-none border-[#E5E5E3] h-10 text-[#111110] hover:bg-[#F0F0EE]"
                        data-testid="invite-close-btn">{result ? "Done" : "Cancel"}</Button>
                    {!result && (
                        <Button onClick={invite} disabled={busy || !selectedIds.length}
                            className="rounded-none h-10 bg-[#111110] hover:bg-[#333333] text-white px-6"
                            data-testid="invite-send-btn">
                            <Send className="w-4 h-4 mr-2" strokeWidth={1.5} /> Invite {selectedIds.length || ""}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
